<template>
    <div class="ai-parameter-select">
        <RedesignBaseSelect
            :value="value"
            :filterable="filterable"
            :multiple="multiple"
            :collapse-tags="collapseTags"
            :placeholder="placeholder"
            :clearable="clearable"
            popper-class="redesign-select-group-dropdown"
            @input="handleInput"
        >
            <template #label>
                {{ label }}
            </template>

            <!-- Grouped options -->
            <el-option-group
                v-for="group in parameterGroups"
                :key="group.label"
                :label="group.label"
            >
                <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label || item.value"
                    :value="item.value"
                />
            </el-option-group>
        </RedesignBaseSelect>
    </div>
</template>

<script setup lang="ts">
import { Option as ElOption, OptionGroup as ElOptionGroup } from 'element-ui'

import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import { TranslateResult } from 'vue-i18n'
import type { ParameterGroup } from '@/types/widgetParameters'

withDefaults(defineProps<{
    value: string | string[]
    label: string | TranslateResult
    placeholder?: string | TranslateResult
    parameterGroups: ParameterGroup[]
    multiple?: boolean
    filterable?: boolean
    collapseTags?: boolean
    clearable?: boolean
}>(), {
    placeholder: '',
    multiple: false,
    filterable: true,
    collapseTags: true,
    clearable: false
})

const emit = defineEmits<{
    (e: 'input', value: string | string[]): void
}>()
function handleInput (val: string | string[]) {
    emit('input', val)
}

</script>

<style lang="scss" scoped>
.ai-parameter-select {
    max-width: 400px;

    :deep(.base-select) {
        .el-select {
            width: 100%;
            max-width: 360px;
        }
    }
}

.selected-items-container {
    max-width: 360px;

    .selected-items-header {
        @apply font-medium;
        color: var(--steel);
    }
}

.selected-items-list {
    @apply flex flex-col gap-1.5;
}

.selected-item {
    @apply flex items-center gap-2 cursor-grab;
    padding: 8px 10px;
    background: var(--mainBgColor-color);
    border: 1px solid var(--ui-lines);
    border-radius: 6px;
    transition: all 0.15s ease;
    user-select: none;

    &:hover {
        border-color: var(--primary-actions);
        background: color-mix(
                in srgb,
                var(--backgroundLight-color) 90%,
                var(--primary-color) 10%
        );
    }

    &:active {
        @apply cursor-grab;
    }

    .drag-handle {
        @apply flex items-center;
        color: var(--steel);

        i {
            font-size: 14px;
        }
    }

    .item-label {
        @apply text-xs;
        flex: 1;
    }

    .remove-btn {
        @apply flex items-center justify-center cursor-pointer;
        color: var(--steel);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        transition: all 0.15s ease;

        &:hover {
            background: var(--primary-actions);
            color: white;
        }
    }
}
</style>
